import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  closeSupportTicket,
  createSupportTicket,
  getSupportTicket,
  listSupportTickets,
  replySupportTicket,
  type SupportTicket,
  type TicketDetail,
} from '../api/supportTickets';

const STATUS: Record<string, { color: string; text: string }> = {
  open: { color: 'orange', text: '待处理' },
  processing: { color: 'blue', text: '处理中' },
  resolved: { color: 'green', text: '已解决' },
  closed: { color: 'default', text: '已关闭' },
};

const PRIORITY: Record<string, { color: string; text: string }> = {
  urgent: { color: 'red', text: '紧急' },
  high: { color: 'volcano', text: '高' },
  normal: { color: 'blue', text: '普通' },
  low: { color: 'default', text: '低' },
};

const TYPE: Record<string, string> = {
  general: '通用',
  sales: '售前咨询',
  tech: '技术支持',
  aftersale: '售后服务',
  complaint: '投诉建议',
};

const RELATED: Record<string, string> = {
  order: '订单',
  subscription: '订阅',
  base: '基座',
  device: '设备',
  project: '项目',
};

const EVENT_LABEL: Record<string, string> = {
  create: '创建工单',
  reply: '回复',
  assign: '分派',
  status_change: '状态变更',
  priority_change: '优先级调整',
  sla_notice: '处理提醒',
};

function fmt(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function MyTicketsTab() {
  const { message: toast } = App.useApp();
  const [items, setItems] = useState<SupportTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSupportTickets({ status, page, pageSize: 10 });
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch {
      toast.error('工单列表加载失败');
    } finally {
      setLoading(false);
    }
  }, [status, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true);
      try {
        setDetail(await getSupportTicket(id));
      } catch {
        toast.error('工单详情加载失败');
      } finally {
        setDetailLoading(false);
      }
    },
    [toast],
  );

  const onCreate = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const ticket = await createSupportTicket(values);
      toast.success(`工单 ${ticket.no} 已创建`);
      setCreateOpen(false);
      form.resetFields();
      setPage(1);
      await load();
      setOpenId(ticket.id);
      await loadDetail(ticket.id);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join('；') : msg || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const onReply = async () => {
    if (!openId || !replyText.trim()) return;
    setReplying(true);
    try {
      await replySupportTicket(openId, replyText.trim());
      setReplyText('');
      toast.success('已回复');
      await loadDetail(openId);
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join('；') : msg || '回复失败');
    } finally {
      setReplying(false);
    }
  };

  const onClose = async () => {
    if (!openId) return;
    try {
      await closeSupportTicket(openId);
      toast.success('工单已关闭');
      await loadDetail(openId);
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || '关闭失败');
    }
  };

  const columns: ColumnsType<SupportTicket> = [
    { title: '工单号', dataIndex: 'no', width: 170, render: (v: string, r) => <a onClick={() => { setOpenId(r.id); setReplyText(''); void loadDetail(r.id); }}>{v}</a> },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '类型', dataIndex: 'type', width: 100, render: (v: string) => TYPE[v] ?? v },
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 90,
      render: (v: string) => <Tag color={PRIORITY[v]?.color}>{PRIORITY[v]?.text ?? v}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => <Tag color={STATUS[v]?.color}>{STATUS[v]?.text ?? v}</Tag>,
    },
    { title: '创建时间', dataIndex: 'createdAt', width: 150, render: (v: string) => fmt(v) },
    {
      title: '操作',
      width: 80,
      render: (_: unknown, r) => (
        <Button size="small" type="link" onClick={() => { setOpenId(r.id); setReplyText(''); void loadDetail(r.id); }}>
          查看
        </Button>
      ),
    },
  ];

  const t = detail?.ticket;
  const canReply = t && t.status !== 'closed';

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Select
          placeholder="状态"
          allowClear
          style={{ width: 120 }}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.text }))}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)} data-testid="ticket-create">
          新建工单
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>
          刷新
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{ current: page, pageSize: 10, total, onChange: setPage }}
      />

      <Modal
        title="新建工单"
        open={createOpen}
        onOk={() => void onCreate()}
        confirmLoading={creating}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={{ type: 'general', priority: 'normal' }}>
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="一句话描述问题" maxLength={200} />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="type" label="类型" style={{ width: 160 }}>
              <Select options={Object.entries(TYPE).map(([value, label]) => ({ value, label }))} />
            </Form.Item>
            <Form.Item name="priority" label="优先级" style={{ width: 140 }}>
              <Select options={Object.entries(PRIORITY).map(([value, s]) => ({ value, label: s.text }))} />
            </Form.Item>
          </Space>
          <Form.Item name="content" label="问题描述">
            <Input.TextArea autoSize={{ minRows: 4, maxRows: 8 }} placeholder="请描述遇到的问题、复现步骤或期望结果（可附截图链接）" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="relatedType" label="关联对象" style={{ width: 160 }}>
              <Select
                allowClear
                placeholder="可选"
                options={Object.entries(RELATED).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
            <Form.Item name="relatedId" label="对象 ID" style={{ width: 220 }}>
              <Input placeholder="订单号 / 项目 ID 等（可选）" />
            </Form.Item>
          </Space>
          <Form.Item name="contact" label="联系方式（可选）">
            <Input placeholder="手机号/邮箱，便于客服联系" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={t ? `工单 ${t.no}` : '工单详情'}
        size={560}
        open={!!openId}
        onClose={() => setOpenId(null)}
        loading={detailLoading}
      >
        {t ? (
          <Space orientation="vertical" size={14} style={{ width: '100%' }}>
            <div>
              <Space wrap>
                <b>{t.title}</b>
                <Tag color={STATUS[t.status]?.color}>{STATUS[t.status]?.text ?? t.status}</Tag>
                <Tag color={PRIORITY[t.priority]?.color}>{PRIORITY[t.priority]?.text ?? t.priority}</Tag>
                <Tag>{TYPE[t.type] ?? t.type}</Tag>
              </Space>
              <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>
                创建：{fmt(t.createdAt)}
                {t.slaDueAt && t.status !== 'closed' ? ` · 承诺处理：${fmt(t.slaDueAt)}` : ''}
                {detail?.assignee ? ` · 处理人：${detail.assignee.name}` : ''}
              </div>
            </div>
            {t.content && (
              <div style={{ background: '#fafafa', borderRadius: 8, padding: 10, whiteSpace: 'pre-wrap' }}>{t.content}</div>
            )}

            <Timeline
              items={(detail?.events ?? []).map((e) => ({
                color: e.action === 'sla_notice' ? 'red' : e.action === 'status_change' ? 'blue' : 'gray',
                children: (
                  <div>
                    <b>{EVENT_LABEL[e.action] ?? e.action}</b>
                    <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                      {e.actorType === 'agent' ? '客服' : e.actorType === 'visitor' ? '我' : '系统'} · {fmt(e.createdAt)}
                    </span>
                    {e.detail?.content != null && (
                      <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{String(e.detail.content)}</div>
                    )}
                    {e.action === 'status_change' && (
                      <div style={{ color: '#666', fontSize: 12 }}>
                        {(STATUS[String(e.detail?.from)]?.text ?? String(e.detail?.from))} →{' '}
                        {(STATUS[String(e.detail?.to)]?.text ?? String(e.detail?.to))}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />

            {canReply ? (
              <div>
                <Input.TextArea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="补充说明或追问（会通知客服）"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  data-testid="ticket-reply"
                />
                <Space style={{ marginTop: 8 }}>
                  <Button type="primary" loading={replying} disabled={!replyText.trim()} onClick={() => void onReply()} data-testid="ticket-reply-send">
                    回复
                  </Button>
                  <Popconfirm title="确认关闭该工单？" onConfirm={() => void onClose()}>
                    <Button danger data-testid="ticket-close">
                      关闭工单
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="工单已关闭，如需帮助请新建工单" />
            )}
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}
